/**
 * KML export for site layers — Google Earth interchange for published
 * generation layouts, mirroring `geoJsonExporter.ts`.
 *
 * Pure string templating (no DOM/XML library): every user-controlled value is
 * XML-escaped, colors are converted from the layer palette's #rrggbb to KML's
 * aabbggrr byte order, and provenance attributes ride along in ExtendedData so
 * a re-import can trace the layer back to its scenario.
 */
import type { SiteLayer } from '../types/site';
import { LAYER_COLORS } from '../config/siteLayerColors';

const POLYGON_FILL_ALPHA = '66'; // ~40%
const LINE_ALPHA = 'ff';

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** #rrggbb (or #rgb) -> KML aabbggrr. Falls back to opaque white. */
export function cssColorToKml(color: string | undefined, alpha: string): string {
  const raw = color?.trim() ?? '';
  const long = /^#([0-9a-f]{6})$/i.exec(raw)?.[1];
  const short = /^#([0-9a-f]{3})$/i.exec(raw)?.[1];
  const rrggbb = long ?? (short ? short.split('').map(ch => ch + ch).join('') : null);
  if (!rrggbb) return `${alpha}ffffff`;
  const rr = rrggbb.slice(0, 2);
  const gg = rrggbb.slice(2, 4);
  const bb = rrggbb.slice(4, 6);
  return `${alpha}${bb}${gg}${rr}`.toLowerCase();
}

function layerColor(layer: SiteLayer): string {
  return layer.color ?? LAYER_COLORS[layer.category] ?? '#ffffff';
}

function coordinateString(points: ReadonlyArray<{ lat: number; lng: number }>): string {
  return points.map(point => `${point.lng},${point.lat},0`).join(' ');
}

function geometryKml(layer: SiteLayer): string | null {
  const points = layer.geometry.coordinates
    .filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng));
  if (layer.geometry.type === 'point') {
    const point = points[0];
    if (!point) return null;
    return `<Point><coordinates>${point.lng},${point.lat},0</coordinates></Point>`;
  }
  if (layer.geometry.type === 'line') {
    if (points.length < 2) return null;
    return `<LineString><tessellate>1</tessellate><coordinates>${coordinateString(points)}</coordinates></LineString>`;
  }
  if (points.length < 3) return null;
  const first = points[0]!;
  const last = points.at(-1)!;
  const ring = first.lat === last.lat && first.lng === last.lng ? points : [...points, first];
  const innerHoles = (layer.geometry.holes ?? [])
    .map((hole) => hole.filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng)))
    .filter((hole) => hole.length >= 3)
    .map((hole) => {
      const hFirst = hole[0]!;
      const hLast = hole.at(-1)!;
      const hRing = hFirst.lat === hLast.lat && hFirst.lng === hLast.lng ? hole : [...hole, hFirst];
      return `<innerBoundaryIs><LinearRing><coordinates>${coordinateString(hRing)}</coordinates></LinearRing></innerBoundaryIs>`;
    })
    .join('');
  return `<Polygon><outerBoundaryIs><LinearRing><coordinates>${coordinateString(ring)}</coordinates></LinearRing></outerBoundaryIs>${innerHoles}</Polygon>`;
}

function extendedData(layer: SiteLayer): string {
  const entries: Array<[string, unknown]> = [
    ['category', layer.category],
    ['source', layer.source],
    ...(layer.description ? [['description', layer.description] as [string, unknown]] : []),
    ...Object.entries(layer.attributes ?? {}),
  ];
  const data = entries
    .filter(([, value]) => value != null && (typeof value !== 'object'))
    .map(([key, value]) => `      <Data name="${escapeXml(key)}"><value>${escapeXml(String(value))}</value></Data>`)
    .join('\n');
  return data ? `    <ExtendedData>\n${data}\n    </ExtendedData>\n` : '';
}

export function siteLayersToKml(layers: readonly SiteLayer[], documentName: string): string {
  const placemarks = layers.flatMap((layer, index) => {
    const geometry = geometryKml(layer);
    if (!geometry) return [];
    const color = layerColor(layer);
    const style = layer.geometry.type === 'polygon'
      ? `    <Style id="style_${index}">
      <LineStyle><color>${cssColorToKml(color, LINE_ALPHA)}</color><width>2</width></LineStyle>
      <PolyStyle><color>${cssColorToKml(color, POLYGON_FILL_ALPHA)}</color><fill>1</fill><outline>1</outline></PolyStyle>
    </Style>`
      : `    <Style id="style_${index}">
      <LineStyle><color>${cssColorToKml(color, LINE_ALPHA)}</color><width>3</width></LineStyle>
      <IconStyle><color>${cssColorToKml(color, LINE_ALPHA)}</color></IconStyle>
    </Style>`;
    return [`${style}
    <Placemark id="${escapeXml(layer.id)}">
      <name>${escapeXml(layer.name)}</name>
${extendedData(layer)}      <styleUrl>#style_${index}</styleUrl>
      ${geometry}
    </Placemark>`];
  }).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapeXml(documentName)}</name>
    <description>Deep Atomic published generation layout export. Screening geometry; not construction CAD.</description>
${placemarks}
  </Document>
</kml>
`;
}

export function downloadSiteLayersKml(layers: readonly SiteLayer[], fileName: string): void {
  const name = fileName.endsWith('.kml') ? fileName : `${fileName}.kml`;
  const blob = new Blob(
    [siteLayersToKml(layers, name.replace(/\.kml$/, ''))],
    { type: 'application/vnd.google-earth.kml+xml' },
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
